import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Alert,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import { Send, AccountBalance, CreditCard, Error as ErrorIcon } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '@/store';
import { useNavigate } from 'react-router-dom';

const CONTACT_EMAIL = import.meta.env.VITE_CONTACT_EMAIL || 'support@banking-dashboard.com';

const Transfer: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { balances } = useSelector((state: RootState) => state.user);
  const { isLoading } = useSelector((state: RootState) => state.user);

  const [transferType, setTransferType] = useState<'account' | 'card'>('account');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [accountForm, setAccountForm] = useState({
    beneficiary: '',
    accountNumber: '',
    bankName: '',
    swiftBic: '',
    country: '',
    address: '',
    phoneNumber: '',
    amount: '',
    currency: 'EUR' as 'EUR' | 'USD',
    description: '',
  });

  const [cardForm, setCardForm] = useState({
    cardType: 'VISA' as 'VISA' | 'MASTER',
    cardNumber: '',
    nameOnCard: '',
    expiryDate: '',
    cvv: '',
    amount: '',
    currency: 'EUR' as 'EUR' | 'USD',
    description: '',
  });

  const handleAccountFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setAccountForm(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCardFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setCardForm(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (e: any, formType: 'account' | 'card') => {
    const { name, value } = e.target;
    if (formType === 'account') {
      setAccountForm(prev => ({
        ...prev,
        [name]: value,
      }));
    } else {
      setCardForm(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // prevent outer handlers from receiving this submit
    // stopPropagation is safe here to avoid accidental navigation
    (e as any).stopPropagation();
    setError(null);
    setSuccess(null);

    const currentForm = transferType === 'account' ? accountForm : cardForm;

    if (!currentForm.amount || parseFloat(currentForm.amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    // Validate account form
    if (transferType === 'account') {
      if (!accountForm.beneficiary || !accountForm.accountNumber || !accountForm.bankName ||
          !accountForm.swiftBic || !accountForm.country || !accountForm.address || !accountForm.phoneNumber) {
        setError('Please fill in all account details');
        return;
      }
    }

    // Validate card form
    if (transferType === 'card') {
      if (!cardForm.cardType || !cardForm.cardNumber || !cardForm.nameOnCard ||
          !cardForm.expiryDate || !cardForm.cvv) {
        setError('Please fill in all card details');
        return;
      }
    }

    // Submit transfer request to backend and show result on this page
    try {
      const token = localStorage.getItem('accessToken');
      const payload = {
        paymentMethod: transferType === 'account' ? 'account' : 'card',
        amount: parseFloat(currentForm.amount as string),
        currency: currentForm.currency,
        details: transferType === 'account' ? accountForm : cardForm,
      };

      const response = await fetch('/api/user/transfer-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      // Backend may return success:true even when transaction is pending/failed
      if (data.success) {
        const tx = data.data?.transaction;
        // If transaction exists and is pending or failed, show the contact message
        if (!tx || tx.status === 'failed' || tx.status === 'pending') {
          setError(`TRANSACTION FAILED. CONTACT ${CONTACT_EMAIL} FOR MORE DETAILS.`);
        } else {
          setSuccess('Transfer submitted successfully!');
        }
      } else {
        setError(data.message || data.error || 'Transfer request failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  const getBalance = (currency: 'EUR' | 'USD') => {
    const balance = balances?.find(b => b.currency === currency);
    return balance ? balance.balance : 0;
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Transfer Funds
        </Typography>

        {/* Transfer Type Selection */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Select Transfer Method
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Chip
              icon={<AccountBalance />}
              label="Account"
              clickable
              color={transferType === 'account' ? 'primary' : 'default'}
              onClick={() => setTransferType('account')}
              sx={{ px: 2, py: 2, fontSize: '1rem' }}
            />
            <Chip
              icon={<CreditCard />}
              label="Card"
              clickable
              color={transferType === 'card' ? 'primary' : 'default'}
              onClick={() => setTransferType('card')}
              sx={{ px: 2, py: 2, fontSize: '1rem' }}
            />
          </Box>
        </Box>

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            icon={<ErrorIcon />}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        <Box component="div">
          {transferType === 'account' ? (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    name="currency"
                    value={accountForm.currency}
                    onChange={(e) => handleSelectChange(e, 'account')}
                    label="Currency"
                    disabled={isLoading}
                  >
                    <MenuItem value="EUR">EUR (Balance: {getBalance('EUR').toFixed(2)})</MenuItem>
                    <MenuItem value="USD">USD (Balance: {getBalance('USD').toFixed(2)})</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Beneficiary"
                  name="beneficiary"
                  value={accountForm.beneficiary}
                  onChange={handleAccountFormChange}
                  required
                  disabled={isLoading}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Account Number"
                  name="accountNumber"
                  value={accountForm.accountNumber}
                  onChange={handleAccountFormChange}
                  required
                  disabled={isLoading}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name of Bank"
                  name="bankName"
                  value={accountForm.bankName}
                  onChange={handleAccountFormChange}
                  required
                  disabled={isLoading}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Swift/BIC"
                  name="swiftBic"
                  value={accountForm.swiftBic}
                  onChange={handleAccountFormChange}
                  required
                  disabled={isLoading}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Country"
                  name="country"
                  value={accountForm.country}
                  onChange={handleAccountFormChange}
                  required
                  disabled={isLoading}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phoneNumber"
                  value={accountForm.phoneNumber}
                  onChange={handleAccountFormChange}
                  required
                  disabled={isLoading}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={accountForm.address}
                  onChange={handleAccountFormChange}
                  multiline
                  rows={2}
                  required
                  disabled={isLoading}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Amount"
                  name="amount"
                  type="number"
                  value={accountForm.amount}
                  onChange={handleAccountFormChange}
                  inputProps={{ min: 0.01, step: 0.01 }}
                  required
                  disabled={isLoading}
                  InputProps={{
                    startAdornment: accountForm.currency === 'EUR' ? '€' : '$',
                  }}
                  helperText={`Available: ${accountForm.currency === 'EUR' ? '€' : '$'}${getBalance(accountForm.currency).toFixed(2)}`}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description (Optional)"
                  name="description"
                  value={accountForm.description}
                  onChange={handleAccountFormChange}
                  multiline
                  rows={3}
                  disabled={isLoading}
                />
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    name="currency"
                    value={cardForm.currency}
                    onChange={(e) => handleSelectChange(e, 'card')}
                    label="Currency"
                    disabled={isLoading}
                  >
                    <MenuItem value="EUR">EUR (Balance: {getBalance('EUR').toFixed(2)})</MenuItem>
                    <MenuItem value="USD">USD (Balance: {getBalance('USD').toFixed(2)})</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Card Type *
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Chip
                    label="VISA"
                    clickable
                    color={cardForm.cardType === 'VISA' ? 'primary' : 'default'}
                    onClick={() => setCardForm(prev => ({ ...prev, cardType: 'VISA' }))}
                    sx={{ px: 2, py: 2 }}
                  />
                  <Chip
                    label="MASTER"
                    clickable
                    color={cardForm.cardType === 'MASTER' ? 'primary' : 'default'}
                    onClick={() => setCardForm(prev => ({ ...prev, cardType: 'MASTER' }))}
                    sx={{ px: 2, py: 2 }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Card Number"
                  name="cardNumber"
                  value={cardForm.cardNumber}
                  onChange={handleCardFormChange}
                  required
                  disabled={isLoading}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Name on Card"
                  name="nameOnCard"
                  value={cardForm.nameOnCard}
                  onChange={handleCardFormChange}
                  required
                  disabled={isLoading}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Expiry Date"
                  name="expiryDate"
                  value={cardForm.expiryDate}
                  onChange={handleCardFormChange}
                  placeholder="MM/YY"
                  required
                  disabled={isLoading}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="CVV"
                  name="cvv"
                  value={cardForm.cvv}
                  onChange={handleCardFormChange}
                  required
                  disabled={isLoading}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Amount"
                  name="amount"
                  type="number"
                  value={cardForm.amount}
                  onChange={handleCardFormChange}
                  inputProps={{ min: 0.01, step: 0.01 }}
                  required
                  disabled={isLoading}
                  InputProps={{
                    startAdornment: cardForm.currency === 'EUR' ? '€' : '$',
                  }}
                  helperText={`Available: ${cardForm.currency === 'EUR' ? '€' : '$'}${getBalance(cardForm.currency).toFixed(2)}`}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description (Optional)"
                  name="description"
                  value={cardForm.description}
                  onChange={handleCardFormChange}
                  multiline
                  rows={3}
                  disabled={isLoading}
                />
              </Grid>
            </Grid>
          )}

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                type="button"
                onClick={handleSubmit}
                variant="contained"
                color="primary"
                startIcon={<Send />}
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={20} /> : 'Submit Transfer'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/dashboard')}
                disabled={isLoading}
              >
                Cancel
              </Button>
            </Box>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
};

export default Transfer;
